import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Input } from 'reactstrap';

import Button from '~components/Button';
import ClickableIcon from '~components/ClickableIcon';
import { faArrowRight } from '~components/icon';
import Modal from '~components/Modal';
import { Row, SortState, TableView } from '~components/table-view-v3';

import { Supplier } from '~support-at-home/models';

import columns from '~support-at-home/forms/supplier-invoices/supplierConfigList';

import {
  createSupplier,
  deleteSupplier,
  getAll as getAllSuppliers,
  updateSupplier,
} from '~support-at-home/actions/suppliers';

import { selectSuppliers, selectSuppliersTotal } from '~support-at-home/selectors/suppliers';

interface ComponentProps {
  isActive: boolean;
}
interface Params {
  [key: string]: any;
}

const SupplierConfig: React.FC<ComponentProps> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const suppliers = useAppSelector(selectSuppliers);
  const total = useAppSelector(selectSuppliersTotal);

  const [data, setData] = useState<Supplier[]>([]);
  const [addCompanyName, setAddCompanyName] = useState<string>('');
  const [addCompanyReference, setAddCompanyReference] = useState<string>('');
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [supplierToDelete, setSupplierToDelete] = useState<string | null>(null);
  const [params, setParams] = useState<Params>({
    limit: 30,
  });

  useEffect(() => {
    if (isActive) load();
  }, [isActive, params]);

  useEffect(() => {
    setData(suppliers);
  }, [suppliers]);

  const load = () => {
    dispatch(getAllSuppliers(params));
  };

  const handleChange = (rows: Row[]) => {
    setData(rows as Supplier[]);
  };

  const handleAddRow = async () => {
    if (addCompanyName && addCompanyReference) {
      const { payload } = await dispatch(
        createSupplier({ name: addCompanyName, account_reference: addCompanyReference }),
      );
      if (payload?.success) load();
    }
  };

  const handleSaveRow = async (row: any) => {
    const { id, name, account_reference } = row;
    const { payload } = await dispatch(updateSupplier({ id, name, account_reference }));
    if (payload?.success) load();
  };

  const handleDelete = async () => {
    const { payload } = await dispatch(deleteSupplier({ id: supplierToDelete, is_deleted: true }));
    if (payload?.success) {
      setIsConfirmOpen(false);
      load();
      setSupplierToDelete(null);
    }
  };

  const handleLoadMore = () => {
    setParams({
      ...params,
      limit: params.limit + 30,
    });
  };

  const handleSort = (sortParams: SortState) => {
    setParams({
      ...params,
      sort: sortParams.map(({ id, direction }) => `${id}.${direction}`),
    });
  };

  return (
    <div>
      <Modal
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        header="Confirm Supplier Delete?"
        data-cy="delete-supplier-confirm-modal"
        footer={
          <>
            <Button size="sm" className="ps-4 pe-4" color="danger" onClick={handleDelete}>
              Delete
            </Button>
            <Button
              size="sm"
              className="ps-4 pe-4"
              onClick={() => {
                setIsConfirmOpen(false);
                setSupplierToDelete(null);
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        <div>
          <div>Are you sure you want to delete this supplier?</div>
          <div className="fw-bold">{suppliers?.find(({ id }: Supplier) => id === supplierToDelete)?.name ?? '-'}</div>
        </div>
      </Modal>
      <div className="util-row">
        <div className="fw-bold">Quick Add</div>
        <Input
          id="add-supplier-name"
          type="text"
          placeholder="Supplier Name"
          className="slim-input"
          onChange={({ target: { value } }) => setAddCompanyName(value)}
        />
        <Input
          id="add-supplier-reference"
          type="text"
          placeholder="Supplier Reference"
          className="slim-input"
          onChange={({ target: { value } }) => setAddCompanyReference(value)}
        />
        <ClickableIcon icon={faArrowRight} id="add-supplier" toolTip="Create Supplier" onClick={handleAddRow} />
      </div>
      <TableView
        id="supplier-config-table"
        columns={columns}
        data={data}
        totalCount={total}
        striped
        allowPagination
        allowSort
        showAddRow={false}
        emptyMessage="No suppliers found"
        canDeleteRow
        onChange={handleChange}
        onSaveRow={handleSaveRow}
        onDeleteRow={(id) => {
          setIsConfirmOpen(true);
          setSupplierToDelete(String(id));
        }}
        onLoadMore={handleLoadMore}
        onSort={handleSort}
      />
    </div>
  );
};

export default SupplierConfig;
